import { useFacilityContext } from '@aspendental/shared-components-web';
import { useMediaQuery, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { getServerSideTranslations } from './utils/get-serverside-translations';

import FindLocation from '@src/components/FindLocation';
import ReasonForVisit from '@src/components/ReasonForVisit';
import Stepper from '@src/components/Stepper';
import TabbedForm from '@src/components/TabbedForm';
import { APP_CONFIGURATIONS, MOBILE_STEPPER_ACTIVE } from '@src/constants';
import { DigitalVisitReasonEnum } from '@src/lib/__generated/api';
import { fetchAppData } from '@src/pages/utils/fetch-app-data';
import getConfigByBrand from '@src/pages/utils/get-config-by-brand';
import getThemeByDomainName from '@src/pages/utils/get-theme-by-domain-name';
import { useGetFacility } from '@src/services/useGetFacility';
import { useAppStore, useFormSectionStore, usePatientInformationFormStore, useReasonForVisitStore } from '@src/store';
import useNeedsBasedQuestionsStore from '@src/store/modules/needs-based-questions.store';
import facilitySupportsLanguageToggle from '@src/utils/facility-enabled-spanish-support';

const visitReasonPaths: { [key in DigitalVisitReasonEnum]: string[] } = require('@src/visit-reason-paths');

const Home = ({
	resultURL,
	graphURL,
	googleURL,
	userEventsURL,
	reasonForVisitFromUrl,
	successCardsCollection,
	googleMapApiKey,
	eapiBaseUrl,
}) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

	const { hasReasonForVisit, setReasonForVisit, setHasReasonForVisit } = useReasonForVisitStore((store) => store);

	const { initializeFormNodes } = useFormSectionStore((store) => store);

	const geoLocation = usePatientInformationFormStore((store) => store.geoLocation);
	const setGeolocation = usePatientInformationFormStore((store) => store.setGeolocation);

	const { latitude, longitude } = geoLocation;

	const facilityDetails = useFacilityContext();
	const setRestUrl = useAppStore((store) => store.setRestUrl);
	const setGraphUrl = useAppStore((store) => store.setGraphUrl);
	const setGoogleUrl = useAppStore((store) => store.setGoogleUrl);
	const setMottoFlow = useAppStore((store) => store.setMottoFlow);
	const setUserEventsUrl = useAppStore((store) => store.setUserEventsUrl);
	const setLanguageToggleEnabled = useAppStore((store) => store.setLanguageToggleEnabled);
	const setGoogleMapApiKey = useAppStore((store) => store.setGoogleMapApiKey);
	const setEapiBaseUrl = useAppStore((store) => store.setEapiBaseUrl);

	useEffect(() => {
		setGoogleUrl(googleURL);
		setRestUrl(resultURL);
		setGraphUrl(graphURL);
		setUserEventsUrl(userEventsURL);
		setGoogleMapApiKey(googleMapApiKey);
		setEapiBaseUrl(eapiBaseUrl);
	}, [
		googleURL,
		resultURL,
		graphURL,
		userEventsURL,
		googleMapApiKey,
		setGoogleUrl,
		setRestUrl,
		setGraphUrl,
		setUserEventsUrl,
		setGoogleMapApiKey,
		setEapiBaseUrl,
		eapiBaseUrl,
	]);

	const setHasNeedsBasedQuestions = useNeedsBasedQuestionsStore((store) => store.setHasNeedsBasedQuestions);
	const router = useRouter();
	const { data: facility } = useGetFacility({ facilityCode: router.query.fc ?? facilityDetails?.facilityCode });
	const { locale } = router;
	const isMottoFlow = reasonForVisitFromUrl === DigitalVisitReasonEnum.ClearAligners;

	if (
		locale === 'es-US' ||
		localStorage.getItem('languageToggle') ||
		facilitySupportsLanguageToggle(facility?.code || '')
	) {
		window.localStorage.setItem('languageToggle', 'YES');
		setLanguageToggleEnabled(true);
	}

	useEffect(() => {
		if (facility?.needsBasedQuestions) {
			setHasNeedsBasedQuestions(true);
		}
		if (facility?.latitude && facility.longitude) {
			setGeolocation(facility.latitude, facility.longitude);
		}
	}, [facility, setGeolocation, setHasNeedsBasedQuestions]);

	useEffect(() => {
		if (reasonForVisitFromUrl) {
			setReasonForVisit(reasonForVisitFromUrl);
			setMottoFlow(isMottoFlow);
			setHasReasonForVisit(true);
		}
	}, [reasonForVisitFromUrl, setReasonForVisit, setHasReasonForVisit]);

	useEffect(() => {
		initializeFormNodes(reasonForVisitFromUrl);
	}, [reasonForVisitFromUrl, initializeFormNodes]);

	if (reasonForVisitFromUrl && latitude + longitude === 0) {
		return <FindLocation />;
	}

	if (hasReasonForVisit || reasonForVisitFromUrl) {
		if (isSmallScreen && MOBILE_STEPPER_ACTIVE) {
			return <Stepper specialFlow={isMottoFlow} successCardsCollection={successCardsCollection} />;
		} else {
			return <TabbedForm specialFlow={isMottoFlow} successCardsCollection={successCardsCollection} />;
		}
	}
	return <ReasonForVisit webFacilityDetails={facilityDetails} />;
};

const removeUrlQueryParams = (url: string) => {
	const queryIndex = url.indexOf('?');
	if (queryIndex < 0) {
		return url;
	}

	return url.substring(0, queryIndex);
};

export const getServerSideProps = async ({ req, locale }) => {
	const domain = req.headers.host;
	const theme = getThemeByDomainName(domain);
	const headerTagTheme = Array.isArray(req.headers['x-tag-theme'])
		? (req.headers['x-tag-theme'] as TagThemeType[])[0]
		: (req.headers['x-tag-theme'] as TagThemeType);
	// Priority: HTTP header x-tag-theme > domain > process.env.TAG_BRAND_THEME
	const tagTheme: TagThemeType = headerTagTheme || theme || process.env.TAG_BRAND_THEME || 'aspendental';
	const brandConfig = getConfigByBrand(tagTheme);
	const appData = await fetchAppData({ appName: brandConfig.contentfulAppId, path: req.url, locale });

	const environment = process.env.ENVIRONMENT_NAME;
	const { services } = APP_CONFIGURATIONS;

	const resultURL = environment === 'prod' ? services.prod.EAPI_REST_URL : services.nonprod.EAPI_REST_URL;
	const graphURL = environment === 'prod' ? services.prod.EAPI_GRAPHQL_URL : services.nonprod.EAPI_GRAPHQL_URL;
	const googleURL = environment === 'prod' ? services.prod.GOOGLE_RESERVE_API : services.nonprod.GOOGLE_RESERVE_API;
	const userEventsURL = environment === 'prod' ? services.prod.USER_EVENTS_URL : services.nonprod.USER_EVENTS_URL;
	const googleMapApiKey =
		environment === 'prod' ? services.prod.GOOGLE_MAP_API_KEY : services.nonprod.GOOGLE_MAP_API_KEY;
	const eapiBaseUrl = environment === 'prod' ? services.prod.EAPI_BASE_URL : services.nonprod.EAPI_BASE_URL;

	let reasonForVisitFromUrl: DigitalVisitReasonEnum | null = null;

	if (req.url !== '/') {
		const urlRoute = removeUrlQueryParams(req.url);

		for (const [key, path] of Object.entries(visitReasonPaths)) {
			if (path.includes(urlRoute.replace(/\//g, ''))) {
				reasonForVisitFromUrl = key as DigitalVisitReasonEnum;
				break;
			}
		}
	}
	let hasIpBasedCoordinates = false;
	let coordinates = {
		latitude: 0,
		longitude: 0,
	};

	if (req.headers['cloudfront-viewer-latitude'] && req.headers['cloudfront-viewer-longitude']) {
		hasIpBasedCoordinates = true;
		coordinates = {
			latitude: parseFloat(req.headers['cloudfront-viewer-latitude'] as string),
			longitude: parseFloat(req.headers['cloudfront-viewer-longitude'] as string),
		};
	}

	return {
		props: {
			resultURL,
			graphURL,
			googleURL,
			userEventsURL,
			eapiBaseUrl,
			reasonForVisitFromUrl,
			googleMapApiKey,
			config: brandConfig,
			user: {
				coordinates,
				hasIpBasedCoordinates,
			},
			...(await getServerSideTranslations(locale)),
			env: {
				ENVIRONMENT_NAME: process.env.ENVIRONMENT_NAME,
			},
			...appData,
		},
	};
};

export default Home;
